// Activity — spec ch.06: "Filterable history of preparations and decisions …
// Tenant-scoped append-only audit events; cursor pagination and redaction. Do
// not expose another office through the log."
//
// The lowest-stakes screen in the app to build and the easiest one to get
// quietly wrong, because it is the screen a reviewer would consult when
// something has gone wrong and they need to know what actually happened. Four
// things follow from that.
//
// **It is read-only, and structurally so.** `app.audit_events` is append-only —
// bk_app holds INSERT and SELECT on it and nothing else — so there is no action
// on this screen at all. Not a disabled button: no control, because there is no
// request the API would accept.
//
// **403 is an answer, not a failure.** contracts/http-api.md reserves 403 for a
// disallowed ACTION, and this endpoint is the worked example: a reviewer asking
// for their own office's log is a confirmed member, so hiding the log behind a
// 404 would tell them something false. The screen therefore has a first-class
// no-access state that explains whose log it is, rather than an empty list or a
// spinner that never resolves.
//
// **An event is not an outcome.** Most audit actions record a decision or a
// request, written in the same transaction as the decision itself — which is
// before anything external has happened. The worker's events are the other
// half: they record what the queue then did, with the SYSTEM as their actor,
// because nobody pressed anything. Even `operation.confirmed` is not where a
// reviewer should read the outcome from — a receipt can say `draft_created`,
// and only GET /operations/{id} reports which. So where an event points at an
// operation this screen still says where the outcome actually lives. This log
// must never become a second place that reports whether the ledger moved,
// because a second place can disagree with the first.
//
// **An actor is a person, or it is nobody.** The log stores an `actor_user_id`
// and GET /users resolves one to an office member. Three answers, and the third
// is the one worth keeping: the current session is "You", a colleague is their
// email address, and an id that resolves to NOBODY stays an id. That last case
// is a member who has left — ch.02's "revocation effective immediately" removes
// them from the roster while their past actions keep their id — and inventing a
// name for them, or writing "a former colleague", would be this screen claiming
// to know something the API did not tell it.
//
// The roster is fetched ONCE per office rather than per event, and a failure to
// fetch it is not a failure of this screen: the events still render, with ids
// where names would have been. A log that refused to load because a convenience
// lookup failed would be the worst possible trade.
//
// **There are no client-side filters.** The demo's toolbar filters an in-page
// array. Filtering a cursor-paginated list in the browser would narrow the page
// rather than the log, and show "2 events" for a log with hundreds — the same
// failure as a count that disagrees with its list. The administration filter is
// here because the ENDPOINT supports it; nothing else is offered.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { AuditEvent, AuditPage, OfficeMember } from '../api/types.ts';
import { actorAvatar } from '../components/ActorAvatar.tsx';
import { ACTOR_KINDS, auditAction } from '../states.ts';
import { administrationFilter, useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'no_access' }
  | { readonly kind: 'error'; readonly message: string }
  | {
      readonly kind: 'ready';
      readonly events: readonly AuditEvent[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
    };

/** Actor id to office member. Empty when the roster could not be read. */
type Roster = ReadonlyMap<string, OfficeMember>;

export function ActivityScreen(): ReactNode {
  const workspace = useWorkspace();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [roster, setRoster] = useState<Roster>(new Map());

  const { token, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;

  useEffect(() => {
    let cancelled = false;
    // Reset first, for the same reason the events do: a name from the previous
    // office must never appear beside this office's rows.
    setRoster(new Map());
    void (async () => {
      try {
        const body = await request<{ users: OfficeMember[] }>(tenantPath(tenantId, '/users'), {
          token,
        });
        if (cancelled) return;
        setRoster(new Map(body.users.map((user) => [user.id, user])));
      } catch {
        // Deliberately silent. The roster is how an id becomes a name; without
        // it the log still says who acted, as an id. Failing the screen because
        // a convenience lookup failed would be the worst possible trade.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, token]);

  const fetchPage = useCallback(
    async (cursor: string | null): Promise<AuditPage> => {
      const params = new URLSearchParams();
      const filter = administrationFilter(administrationId);
      if (filter !== undefined) params.set('administrationId', filter);
      if (cursor !== null) params.set('cursor', cursor);
      const query = params.toString();
      return request<AuditPage>(tenantPath(tenantId, `/audit${query === '' ? '' : `?${query}`}`), {
        token,
      });
    },
    [tenantId, token, administrationId],
  );

  useEffect(() => {
    let cancelled = false;
    // Reset FIRST. Changing office or administration must not leave the
    // previous selection's events on screen while the next request is in
    // flight: an event listed under the wrong office is exactly the leak ch.06
    // means by "do not expose another office through the log", even when it is
    // only the browser that got it wrong.
    setLoad({ kind: 'loading' });
    void (async () => {
      try {
        const page = await fetchPage(null);
        if (cancelled) return;
        setLoad({
          kind: 'ready',
          events: page.events,
          nextCursor: page.nextCursor,
          loadingMore: false,
        });
      } catch (caught) {
        if (cancelled) return;
        setLoad(failure(caught));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (load.kind !== 'ready' || load.nextCursor === null || load.loadingMore) return;
    const cursor = load.nextCursor;
    setLoad({ ...load, loadingMore: true });
    try {
      const page = await fetchPage(cursor);
      setLoad((current) =>
        current.kind === 'ready'
          ? {
              kind: 'ready',
              // Appended, never merged by id: the endpoint's sort is total —
              // (occurred_at, id) — so a cursor page cannot repeat a row, and
              // deduplicating here would hide it if it ever did.
              events: [...current.events, ...page.events],
              nextCursor: page.nextCursor,
              loadingMore: false,
            }
          : current,
      );
    } catch (caught) {
      setLoad(failure(caught));
    }
  }, [load, fetchPage]);

  return (
    <>
      <div className="heading">
        <h1>Activity</h1>
        <p>Preparations and decisions stay traceable. This log is a record, not a control.</p>
      </div>

      {load.kind === 'no_access' ? (
        <NoAccess role={workspace.membership.role} />
      ) : load.kind === 'error' ? (
        <div className="card">
          <div className="card-body">
            <p className="error-banner" role="alert">
              {load.message}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-head">
            <div>
              <h2>Activity and decisions</h2>
              <p>Newest first, for the office you are in</p>
            </div>
            <span className="badge">
              {load.kind === 'ready' ? `${load.events.length} shown` : 'Loading'}
            </span>
          </div>

          {load.kind === 'loading' ? (
            <div className="empty">
              <p>Reading the activity log…</p>
            </div>
          ) : load.events.length === 0 ? (
            <div className="empty">
              <h3>Nothing recorded yet</h3>
              <p>Decisions appear here as they are made.</p>
            </div>
          ) : (
            load.events.map((event) => (
              <Event key={event.id} event={event} roster={roster} />
            ))
          )}

          <div className="card-foot between">
            <span>
              Append-only. Nothing on this screen can change what happened, and neither can
              anything else.
            </span>
            {load.kind === 'ready' && load.nextCursor !== null ? (
              <button
                type="button"
                className="btn"
                disabled={load.loadingMore}
                onClick={() => void loadMore()}
              >
                {load.loadingMore ? 'Loading…' : 'Load older'}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}

function Event({
  event,
  roster,
}: {
  readonly event: AuditEvent;
  readonly roster: Roster;
}): ReactNode {
  const workspace = useWorkspace();
  const action = auditAction(event.action);
  const administration =
    event.administrationId === null
      ? null
      : workspace.administrations.find((entry) => entry.id === event.administrationId);

  const providers = new Map(
    workspace.administrations.map((entry) => [entry.id, entry.connection?.provider ?? null]),
  );
  const avatar = actorAvatar(event, roster, providers);
  return (
    <div className="row activity-row">
      <span className={`fa ${avatar.tone}`} title={avatar.name} aria-hidden="true">
        {avatar.initials}
      </span>
      <div className="grow">
        <strong>{action.label}</strong>
        <p>
          {/* Who. Three answers and no fourth: this session, a colleague the
              roster names, or the raw id for somebody who is no longer a member
              of this office. Nothing here invents a person. */}
          <Actor event={event} roster={roster} me={workspace.me.user.id} />
          {' · '}
          {event.administrationId === null
            ? 'Office-wide'
            : (administration?.name ?? event.administrationId)}
          {' · '}
          {format.dateTime(event.occurredAt)}
        </p>
        {action.note === undefined ? null : <p>{action.note}</p>}
        {event.targetKind === 'external_operation' ? (
          <p>
            {/* The rule this screen exists to keep: an event about an operation
                says what was decided, and points at the operation for what
                happened. */}
            What the accounting package did with it is reported by the operation, not by this
            log.
          </p>
        ) : null}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span className="badge">{event.targetKind.replace(/_/g, ' ')}</span>
        {event.correlationId === null ? null : (
          <p className="muted small" style={{ marginTop: 6 }}>
            <code style={{ fontSize: 11 }}>{event.correlationId}</code>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Who acted, resolved as far as the API allows and no further.
 *
 * A system actor has no user at all — migration 0005 makes the two mutually
 * exclusive — so it is reported by its KIND, which is the true statement:
 * nobody pressed anything.
 *
 * An id that the roster does not know is shown as itself. That is a member who
 * has left, and both plausible improvements are wrong: inventing a name would
 * be false, and writing "a former colleague" would assert a reason this screen
 * cannot know — the roster is also empty when the lookup simply failed.
 */
function Actor({
  event,
  roster,
  me,
}: {
  readonly event: AuditEvent;
  readonly roster: Roster;
  readonly me: string;
}): ReactNode {
  if (event.actorUserId === null) return <>{ACTOR_KINDS[event.actorKind] ?? event.actorKind}</>;
  if (event.actorUserId === me) return <>You</>;
  const member = roster.get(event.actorUserId);
  if (member !== undefined) return <>{member.email}</>;
  return (
    <code title="This actor is no longer a member of this office" style={{ fontSize: 11 }}>
      {event.actorUserId}
    </code>
  );
}

/**
 * The answer to a 403 on this endpoint, in the screen's own words.
 *
 * Not an error and not an empty list. The caller is a member of this office —
 * every other screen proves it — and what they lack is the permission to
 * perform this particular reading. Saying so is the whole reason the API
 * answers 403 here instead of 404.
 */
function NoAccess({ role }: { readonly role: string }): ReactNode {
  return (
    <div className="card">
      <div className="empty">
        <h3>You do not have access to the activity log</h3>
        <p>
          The activity log is for office owners and agency operators. Your role in this office is{' '}
          <b>{role.replace(/_/g, ' ')}</b>, which can review and approve work but cannot read the
          office-wide log.
        </p>
        <p style={{ marginTop: 10 }}>
          The history of a single document is on the document itself, and that is not restricted.
        </p>
      </div>
    </div>
  );
}

function failure(caught: unknown): Load {
  if (caught instanceof ApiError && caught.status === 403) return { kind: 'no_access' };
  if (caught instanceof ApiError && caught.isAbsent) {
    // 404 here means the OFFICE is not available to this session — the tenant
    // context is established before the role is considered, so this is not a
    // permission answer and must not be shown as one.
    return { kind: 'error', message: 'This office is not available to your session.' };
  }
  return { kind: 'error', message: 'The activity log could not be read.' };
}
